import {appendQueryString} from "@pg-mono/request";
import {rpAppLink} from "@pg-mono/rp-routes";
import {INavLeftElement} from "@pg-rp/navigation";

import {academyRealEstateLink, bigDataLink, competitionMonitoringLink, voxCrmLink} from "./links";
import {NAVIGATION_REGIONS} from "./navigation_regions";
import {PRICES_CITIES} from "./prices_cities";

const NAV_SECOND_LEVEL_FLATS: INavLeftElement[] = NAVIGATION_REGIONS.map((region) => {
    const title = region.name;
    const url = rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${region.suffix}`});

    return {
        title,
        url,
        gtmData: {
            level: "3",
            text: title
        },
        subElements: region.cities_flats.map((city) => {
            const cityTitle = `Mieszkania ${city.name}`;
            const cityUrl = rpAppLink.offer.listFriendly({friendlySlug: `nowe-mieszkania-${city.suffix}`});
            return {
                title: cityTitle,
                url: cityUrl,
                gtmData: {
                    level: "4",
                    text: cityTitle
                }
            };
        })
    };
});

const NAV_SECOND_LEVEL_HOUSES: INavLeftElement[] = NAVIGATION_REGIONS.map((region) => {
    const title = `Domy ${region.name}`;
    const url = rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${region.suffix}`});
    return {
        title,
        url,
        gtmData: {
            level: "3",
            text: title
        },
        subElements: region.cities_houses.map((city) => {
            const cityTitle = `Domy ${city.name}`;
            const cityUrl = rpAppLink.offer.listFriendly({friendlySlug: `nowe-domy-${city.suffix}`});
            return {
                title: cityTitle,
                url: cityUrl,
                gtmData: {
                    level: "4",
                    text: cityTitle
                }
            };
        })
    };
});

const NAV_SECOND_LEVEL_COMMERCIAL: INavLeftElement[] = [
    {name: "Lokale użytkowe Warszawa", suffix: "warszawa"},
    {name: "Lokale użytkowe Łódź", suffix: "lodz"},
    {name: "Lokale użytkowe Wrocław", suffix: "wroclaw"},
    {name: "Lokale użytkowe Poznań", suffix: "poznan"},
    {name: "Lokale użytkowe Kraków", suffix: "krakow"},
    {name: "Lokale użytkowe Gdańsk", suffix: "gdansk"},
    {name: "Lokale użytkowe Katowice", suffix: "katowice"}
].map((city) => {
    const title = city.name;
    const url = rpAppLink.offer.listFriendly({friendlySlug: `nowe-lokale-uzytkowe-${city.suffix}`});
    return {
        title,
        url,
        gtmData: {
            level: "3",
            text: title
        }
    };
});

const NAV_SECOND_LEVEL_DEVELOPERS: INavLeftElement[] = [
    {name: "Deweloperzy Warszawa", suffix: "warszawa"},
    {name: "Deweloperzy Kraków", suffix: "krakow"},
    {name: "Deweloperzy Białystok", suffix: "bialystok"},
    {name: "Deweloperzy Bydgoszcz", suffix: "bydgoszcz"},
    {name: "Deweloperzy Wrocław", suffix: "wroclaw"},
    {name: "Deweloperzy Poznań", suffix: "poznan"},
    {name: "Deweloperzy Gdańsk", suffix: "gdansk"},
    {name: "Deweloperzy Lublin", suffix: "lublin"},
    {name: "Deweloperzy Toruń", suffix: "torun"},
    {name: "Deweloperzy Rzeszów", suffix: "rzeszow"},
    {name: "Deweloperzy Szczecin", suffix: "szczecin"},
    {name: "Deweloperzy Łódź", suffix: "lodz"}
].map((city) => {
    const title = city.name;
    const url = rpAppLink.vendor.listFriendly({friendlySlug: city.suffix});
    return {
        title,
        url,
        gtmData: {
            level: "3",
            text: title
        }
    };
});

const NAV_THIRD_LEVEL_INVEST_FLATS: INavLeftElement[] = [
    {name: "Apartamenty inwestycyjne Warszawa", suffix: "warszawa"},
    {name: "Apartamenty inwestycyjne Gdańsk", suffix: "gdansk"},
    {name: "Apartamenty inwestycyjne Kraków", suffix: "krakow"},
    {name: "Apartamenty inwestycyjne Łódź", suffix: "lodz"},
    {name: "Apartamenty inwestycyjne Poznań", suffix: "poznan"},
    {name: "Apartamenty inwestycyjne Wrocław", suffix: "wroclaw"}
].map((category) => {
    const title = category.name;
    const url = rpAppLink.investmentOffer.investmentCategory.subcategory.base({category: "apartamenty-inwestycyjne", subcategory: category.suffix});
    return {
        title,
        url,
        gtmData: {
            level: "4",
            text: title
        }
    };
});

const NAV_THIRD_LEVEL_INVEST_HOLIDAY_FLATS: INavLeftElement[] = [
    {name: "Apartamenty nad morzem", suffix: "nad-morzem"},
    {name: "Apartamenty nad jeziorem", suffix: "nad-jeziorem"},
    {name: "Apartamenty w górach", suffix: "w-gorach"}
].map((category) => {
    const title = category.name;
    const url = rpAppLink.investmentOffer.investmentCategory.subcategory.base({category: "apartamenty-wakacyjne", subcategory: category.suffix});
    return {
        title,
        url,
        gtmData: {
            level: "4",
            text: title
        }
    };
});

const NAV_SECOND_LEVEL_INVEST: INavLeftElement[] = [
    {name: "Condohotele", suffix: "condohotel"},
    {name: "Apartamenty inwestycyjne", suffix: "apartamenty-inwestycyjne", subElements: NAV_THIRD_LEVEL_INVEST_FLATS},
    {name: "Apartamenty wakacyjne", suffix: "apartamenty-wakacyjne", subElements: NAV_THIRD_LEVEL_INVEST_HOLIDAY_FLATS},
    {name: "Nieruchomości Hiszpania", suffix: "za-granica", subcategory: "hiszpania"}
].map((category) => {
    const title = category.name;
    const baseUrl = category.suffix && rpAppLink.investmentOffer.investmentCategory.base({category: category.suffix});
    const urlWithSubcategory = category.subcategory
        ? rpAppLink.investmentOffer.investmentCategory.subcategory.base({category: category.suffix, subcategory: category.subcategory})
        : null;
    const url = urlWithSubcategory || baseUrl;
    return {
        title,
        url,
        gtmData: {
            level: "3",
            text: title
        },
        subElements: category?.subElements
    };
});

const NAV_FIRST_LEVEL = [
    {
        title: "Nowe mieszkania",
        url: rpAppLink.offer.listFriendly({friendlySlug: "nowe-mieszkania"}),
        gtmData: {
            level: "2",
            text: "Nowe mieszkania"
        },
        subElements: NAV_SECOND_LEVEL_FLATS
    },
    {
        title: "Nowe domy",
        url: rpAppLink.offer.listFriendly({friendlySlug: "nowe-domy"}),
        gtmData: {
            level: "2",
            text: "Nowe domy"
        },
        subElements: NAV_SECOND_LEVEL_HOUSES
    },
    {
        title: "Nowe lokale użytkowe",
        url: rpAppLink.offer.listFriendly({friendlySlug: "nowe-lokale-uzytkowe"}),
        gtmData: {
            level: "2",
            text: "Nowe lokale użytkowe"
        },
        subElements: NAV_SECOND_LEVEL_COMMERCIAL
    },
    {
        title: "Deweloperzy",
        url: rpAppLink.vendor.list(),
        gtmData: {
            level: "2",
            text: "Deweloperzy"
        },
        subElements: NAV_SECOND_LEVEL_DEVELOPERS
    },
    {
        title: "Nieruchomości inwestycyjne",
        url: rpAppLink.investmentOffer.landingPage(),
        gtmData: {
            level: "2",
            text: "Nieruchomości inwestycyjne"
        },
        subElements: NAV_SECOND_LEVEL_INVEST
    },
    {
        title: "Ranking inwestycji",
        url: rpAppLink.ranking.base(),
        gtmData: {
            level: "2",
            text: "Ranking inwestycji"
        }
    },
    {
        title: "Konfigurator mieszkania",
        url: rpAppLink.propertyConfigurator(),
        gtmData: {
            level: "2",
            text: "Konfigurator mieszkania"
        }
    },
    {
        title: "Doradca mieszkaniowy",
        url: rpAppLink.weFindForYou(),
        gtmData: {
            level: "2",
            text: "Doradca mieszkaniowy"
        }
    }
];

export const NAV_SECOND_LEVEL_FOR_DEVELOPERS: INavLeftElement[] = [
    {
        title: `Ustawa o jawności cen`,
        url: "https://jawnosc-cen.rynekpierwotny.pl/",
        gtmData: {
            level: "2",
            text: "Ustawa o jawności cen"
        }
    },
    {
        title: "Sprzedawaj więcej!",
        url: rpAppLink.ourOffer(),
        gtmData: {
            level: "2",
            text: "Sprzedawaj więcej!"
        }
    },
    {
        title: "Akademia Real Estate",
        url: academyRealEstateLink,
        gtmData: {
            level: "2",
            text: "Akademia Real Estate"
        },
        target: "_blank"
    },
    {
        title: "Monitoring konkurencji",
        url: competitionMonitoringLink,
        gtmData: {
            level: "2",
            text: "Monitoring konkurencji"
        },
        target: "_blank"
    },
    {
        title: "Big Data",
        url: appendQueryString(bigDataLink, {
            utm_campaign: "RP_Linkowanie",
            utm_medium: "menu",
            utm_source: "rynekpierwotny"
        }),
        gtmData: {
            level: "2",
            text: "Big Data"
        },
        target: "_blank"
    },
    {
        title: "CRM dla deweloperów",
        url: voxCrmLink,
        gtmData: {
            level: "2",
            text: "CRM dla deweloperów"
        },
        target: "_blank"
    }
];

export const NAV_SECOND_LEVEL_FLAT_PRICES: INavLeftElement[] = [
    {
        title: `Ustawa o jawności cen`,
        url: "https://jawnosc-cen.rynekpierwotny.pl/",
        gtmData: {
            level: "2",
            text: "Ustawa o jawności cen"
        }
    },
    ...PRICES_CITIES.map((city) => {
        return {
            title: `Ceny mieszkań ${city.name}`,
            url: rpAppLink.prices.flats.city({regionSlug: city.slug}),
            gtmData: {
                level: "2",
                text: `Ceny mieszkań ${city.name}`
            }
        };
    })
];

export const NAV_SECOND_LEVEL_FOR_CREDITS = [
    {
        title: "Kalkulator zdolności kredytowej",
        url: rpAppLink.credits.rating(),
        gtmData: {
            level: "2",
            text: "Kalkulator zdolności kredytowej"
        }
    },
    {
        title: "Kalkulator kredytu hipotecznego",
        url: rpAppLink.credits.offers(),
        gtmData: {
            level: "2",
            text: "Kalkulator kredytu hipotecznego"
        }
    }
];

export const NAV_MAIN_ELEMENTS: INavLeftElement[] = [
    {
        title: "Nieruchomości",
        url: rpAppLink.offer.listFriendly({friendlySlug: "nowe-mieszkania-i-domy"}),
        gtmData: {
            level: "1",
            text: "Nieruchomości"
        },
        subElements: NAV_FIRST_LEVEL
    },
    {
        title: "Wykończenie wnętrz",
        url: rpAppLink.architect.list(),
        gtmData: {
            level: "1",
            text: "Wykończenie wnętrz"
        }
    },
    {
        title: "Kredyty",
        url: rpAppLink.credits.base(),
        gtmData: {
            level: "1",
            text: "Kredyty"
        },
        subElements: NAV_SECOND_LEVEL_FOR_CREDITS
    },
    {
        title: "Dla poszukującego",
        url: rpAppLink.article.list(),
        gtmData: {
            level: "1",
            text: "Dla poszukującego"
        }
    },
    {
        title: "Dla dewelopera",
        url: rpAppLink.ourOffer(),
        gtmData: {
            level: "1",
            text: "Dla dewelopera"
        },
        subElements: NAV_SECOND_LEVEL_FOR_DEVELOPERS
    },
    {
        title: "Ceny mieszkań",
        url: rpAppLink.prices.flats.base(),
        gtmData: {
            level: "1",
            text: "Ceny mieszkań"
        },
        subElements: NAV_SECOND_LEVEL_FLAT_PRICES
    }
];
